import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Leaf, Check } from 'lucide-react';
import GreenButton from '../../home/GreenButton';
import { StyleSummary, formatGbp, priceFrom, swatchFromRgb, cleanText, cleanBrand, sortSizes } from '../../../lib/clothingBrowseApi';

const GREEN = '#78BE20';

interface QuickLookProps {
  style: StyleSummary;
  onClose: () => void;
}

const Fact: React.FC<{ label: string; value?: string | null }> = ({ label, value }) =>
  value ? (
    <div>
      <dt className="font-poppins text-[10px] font-semibold tracking-[0.14em] uppercase text-black/45">{label}</dt>
      <dd className="font-poppins text-[13px] text-black/85 mt-0.5">{value}</dd>
    </div>
  ) : null;

const QuickLook: React.FC<QuickLookProps> = ({ style, onClose }) => {
  const variants = style.color_variants || [];
  const [selected, setSelected] = useState(0);
  useEffect(() => { setSelected(0); }, [style.style_code]);

  // Escape closes, and the page behind must not scroll while the drawer is over it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const current = variants[selected];
  const image = current?.colourImage || current?.primaryImage || style.primary_product_image_url || '';
  const price = priceFrom(style);
  const priceMax = parseFloat(style.price_max || '');
  const sizes = sortSizes(style.available_sizes || []);
  const accreditations = (style.accreditations || '').replace(/�/g, '').split('|').map((s) => s.trim()).filter(Boolean);
  const description = cleanText(style.retail_description);
  const detailsTo = `/products/${encodeURIComponent(style.style_code)}`;

  return (
    // Drawer, not an inline panel: it slides in from the right on desktop and
    // tablet landscape, and up from the bottom on phones and tablet portrait.
    // 1024px is the split, so an iPad is a side drawer landscape and a sheet portrait.
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label={`Quick look: ${cleanText(style.style_name)}`}>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close quick look"
        className="absolute inset-0 w-full h-full bg-black/45 quicklook-backdrop cursor-default"
      />

      <section
        className="
          absolute flex flex-col bg-[#f7f7f7] overscroll-contain
          inset-x-0 bottom-0 max-h-[88dvh] rounded-t-2xl quicklook-panel
          lg:inset-y-0 lg:right-0 lg:left-auto lg:bottom-auto lg:max-h-none lg:h-full
          lg:w-[min(560px,92vw)] lg:rounded-t-none lg:rounded-l-2xl
        "
      >
        {/* Grab handle, phones only */}
        <span aria-hidden className="lg:hidden shrink-0 self-center w-10 h-1 rounded-full bg-black/15 mt-2.5" />

        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white border border-black/10 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
          aria-label="Close quick look"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Identity stays at the top of the drawer: brand, name and price are what
            the customer opened this for, so they must not sit below a tall photo. */}
        <header className="shrink-0 min-w-0 px-5 pt-4 pb-4 pr-16 lg:px-7 lg:pt-6 border-b border-black/5">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-poppins text-[11px] font-semibold tracking-[0.14em] uppercase text-black/55">{cleanBrand(style.brand)}</span>
            <span className="font-poppins text-[11px] text-black/35 tabular-nums">{style.style_code}</span>
            {style.sustainable_organic === 'Yes' && (
              <span className="inline-flex items-center gap-1 font-poppins text-[11px] font-semibold" style={{ color: '#2f7a12' }}>
                <Leaf className="w-3.5 h-3.5" /> Organic or recycled
              </span>
            )}
          </div>
          <h3 className="font-montserrat font-extrabold text-[20px] lg:text-[24px] leading-tight tracking-[-0.02em] mt-1">
            {cleanText(style.style_name)}
          </h3>
          <p className="font-poppins mt-1.5 text-[15px]">
            {price !== null ? (
              <>
                <span className="text-black/50 text-[12px]">Garment from </span>
                <span className="font-semibold text-[18px]">{formatGbp(price)}</span>
                {Number.isFinite(priceMax) && priceMax > price && (
                  <span className="text-black/50 text-[12px]"> to {formatGbp(priceMax)} by size</span>
                )}
              </>
            ) : 'Price on enquiry'}
            <span className="text-black/45 text-[12px]"> · decoration quoted separately</span>
          </p>
        </header>

        {/* Only this middle band scrolls, so the header and the CTA stay put. */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 lg:px-7">
          {/* Capped, not square: a full-width square photo pushed everything else
              below the fold of the drawer. */}
          <div className="relative h-[min(38vh,300px)] rounded-xl bg-white overflow-hidden">
            {image && (
              <img key={image} src={image} alt={`${cleanText(style.style_name)} in ${current?.colourName || 'default colour'}`} className="absolute inset-0 w-full h-full object-contain p-4 mix-blend-multiply" />
            )}
            {current && (
              <span className="absolute bottom-3 left-3 h-7 px-3 rounded-full bg-black/80 text-white font-poppins text-[11px] font-medium inline-flex items-center">
                {current.colourName}
              </span>
            )}
          </div>

          <div className="min-w-0 flex flex-col">
          {description && (
            <p className="font-poppins text-[13px] leading-6 text-black/75 mt-3 line-clamp-3">{description}</p>
          )}

          {variants.length > 0 && (
            <div className="mt-4">
              <p className="font-poppins text-[10px] font-semibold tracking-[0.14em] uppercase text-black/45 mb-2">
                {variants.length} colour{variants.length === 1 ? '' : 's'}
              </p>
              <ul className="flex flex-wrap gap-1.5">
                {variants.map((v, i) => (
                  <li key={v.colourCode}>
                    <button
                      type="button"
                      onClick={() => setSelected(i)}
                      title={v.colourName}
                      aria-label={v.colourName}
                      aria-pressed={i === selected}
                      className={`w-6 h-6 rounded-full border border-black/10 flex items-center justify-center transition-transform hover:scale-110 ${i === selected ? 'ring-2 ring-offset-2 ring-[#78BE20]' : ''}`}
                      style={{ background: swatchFromRgb(v.rgb) }}
                    >
                      {i === selected && <Check className="w-3 h-3 text-white mix-blend-difference" />}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {sizes.length > 0 && (
            <div className="mt-4">
              <p className="font-poppins text-[10px] font-semibold tracking-[0.14em] uppercase text-black/45 mb-2">Sizes</p>
              <ul className="flex flex-wrap gap-1.5">
                {sizes.map((s) => (
                  <li key={s} className="h-7 px-2.5 rounded-md bg-white border border-black/10 font-poppins text-[12px] inline-flex items-center">{s}</li>
                ))}
              </ul>
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 mt-5">
            <Fact label="Fit" value={[style.gender === 'Male' ? 'Men' : style.gender === 'Female' ? 'Women' : style.gender, style.age_group].filter(Boolean).join(' · ')} />
            <Fact label="Type" value={style.product_type} />
            <div className="col-span-2">
              <Fact label="Fabric" value={cleanText(style.fabric)} />
            </div>
            {accreditations.length > 0 && (
              <div className="col-span-2">
                <Fact label="Accreditations" value={accreditations.join(', ')} />
              </div>
            )}
          </dl>
          </div>
        </div>

        {/* CTA pinned to the bottom of the drawer, not buried under the facts. */}
        <footer className="shrink-0 border-t border-black/10 bg-white/70 px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] lg:px-7">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <GreenButton to={detailsTo}>Full details &amp; enquire</GreenButton>
            <Link to={`/clothing?brands=${encodeURIComponent(style.brand)}`} className="font-poppins text-[12px] font-semibold tracking-[0.1em] uppercase hover:underline" style={{ color: GREEN }}>
              More from {cleanBrand(style.brand)}
            </Link>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default QuickLook;
